/**
 * Baby cost calculator UI bindings (esbuild entry point).
 *
 * Holds the item list in memory, renders an editable table + controls, and
 * re-computes the stacked cumulative chart on every change. All cost math lives
 * in baby.ts (pure); this file only does DOM read/write + Chart.js setup.
 *
 * State is mirrored to the URL query params for shareable links.
 */

import { formatCurrency, copyShareableUrl, getQueryParams } from '../shared/calculator';
import type {
  BabyCalcResult,
  BabyItem,
  BabyItemKind,
  BabyRatePeriod,
} from '../shared/types';
import { calculateBabyCosts, decodeItems, encodeItems, groupSmallSeries } from './baby';
import {
  DEFAULT_BABY_ITEMS,
  DEFAULT_WINDOW_END,
  DEFAULT_WINDOW_START,
  newCustomId,
} from './defaults';

/* ------------------------------ chart shim ------------------------------ */

interface ChartInstance {
  destroy(): void;
}
interface ChartDataset {
  label: string;
  data: number[];
  [k: string]: unknown;
}
interface ChartConfig {
  type: string;
  data: { labels: string[]; datasets: ChartDataset[] };
  options: Record<string, unknown>;
}
declare const Chart: new (ctx: HTMLCanvasElement, config: ChartConfig) => ChartInstance;

/* ------------------------------ DOM handles ----------------------------- */

const windowStartInput = document.getElementById('window-start') as HTMLInputElement;
const windowEndInput = document.getElementById('window-end') as HTMLInputElement;
const itemsTbody = document.querySelector('#items-table tbody') as HTMLTableSectionElement;
const grandTotalEl = document.getElementById('grand-total') as HTMLParagraphElement;
const addRecurringBtn = document.getElementById('add-recurring') as HTMLButtonElement;
const addOnetimeBtn = document.getElementById('add-onetime') as HTMLButtonElement;

/* ------------------------------ state ----------------------------------- */

/** Mutable working copy of the items list. */
let items: BabyItem[] = DEFAULT_BABY_ITEMS.map((it) => ({ ...it }));
let chart: ChartInstance | null = null;

/** Ids of rows added since the last render — used to play the entrance animation once. */
const newRowIds = new Set<string>();

/**
 * ColorBrewer "Dark2" qualitative palette, ordered light/dark-alternating for
 * maximum edge contrast between adjacent stacked bands (the failure mode of
 * Okabe-Ito in a stacked area: neighboring bands of similar luminance fuse).
 * Source: https://colorbrewer2.org/#type=qualitative&scheme=Dark2&n=8
 * Grey #B0B0B0 reserved for the synthetic "Other" band (groupSmallSeries).
 */
const PALETTE = ['#1B9E77', '#D95F02', '#7570B3', '#E7298A', '#66A61E', '#E6AB02', '#A6761D', '#66A61E'];
const OTHER_COLOR = '#B0B0B0';
function colorFor(idx: number): string {
  return PALETTE[idx % PALETTE.length]!;
}

/** Premium easing curve (Linear/Vercel feel) shared by toggles, rows, chart. */
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

/* ------------------------------ helpers --------------------------------- */

function readWindow(): { start: number; end: number } {
  const start = Math.max(0, Math.floor(Number(windowStartInput.value) || 0));
  const end = Math.max(start + 1, Math.floor(Number(windowEndInput.value) || DEFAULT_WINDOW_END));
  return { start, end };
}

/**
 * Serialize the full state (window + every item field) into a compact URL param
 * set, so the shareable link reproduces the exact calculator state. Items are
 * encoded via the pure `encodeItems` (URL-encoded JSON) — one `items` param,
 * keeps the URL manageable and handles arbitrary custom-item counts generically.
 */
function encodeState(): Record<string, string> {
  const { start, end } = readWindow();
  return {
    wstart: String(start),
    wend: String(end),
    items: encodeItems(items),
  };
}

/**
 * Reflect the current state into the URL (replaceState, no extra history
 * entries) so a refresh or copied URL reproduces the state exactly.
 */
function syncUrl(): void {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(encodeState())) params.set(k, v);
  const url = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, '', url);
}

/** Format months for display: "12mo", "2y", "2y 6mo". */
function formatMonth(m: number): string {
  if (m < 12) return `${m}mo`;
  const y = Math.floor(m / 12);
  const mo = m % 12;
  return mo === 0 ? `${y}y` : `${y}y ${mo}mo`;
}

/* ------------------------------ rendering ------------------------------- */

function renderItemsTable(): void {
  itemsTbody.innerHTML = items
    .map((item, idx) => {
      const isOnetime = item.kind === 'onetime';
      const rateCell = isOnetime
        ? '<td class="fn-muted" data-label="Rate">—</td>'
        : `<td class="fn-rate" data-label="Rate">
            <input type="number" min="0" step="0.1" value="${item.rate}" data-field="rate" data-idx="${idx}">
            <select data-field="ratePeriod" data-idx="${idx}">
              <option value="day"${item.ratePeriod === 'day' ? ' selected' : ''}>/day</option>
              <option value="week"${item.ratePeriod === 'week' ? ' selected' : ''}>/week</option>
              <option value="month"${item.ratePeriod === 'month' ? ' selected' : ''}>/month</option>
            </select>
          </td>`;
      const endCell = isOnetime
        ? '<td class="fn-muted" data-label="End mo">one-time</td>'
        : `<td data-label="End mo"><input type="number" min="-1" step="1" value="${item.endMonth}" data-field="endMonth" data-idx="${idx}"></td>`;

      const labelCell = item.custom
        ? `<td data-label="Item"><input type="text" value="${escapeHtml(item.label)}" data-field="label" data-idx="${idx}" class="fn-item-label"></td>`
        : `<td data-label="Item"><span class="fn-item-name">${escapeHtml(item.label)}</span>${
            item.help
              ? `<span class="fn-help" tabindex="0" role="button" aria-label="More info about ${escapeHtml(item.label)}"><span class="fn-help-icon">ⓘ</span><span class="fn-help-tip">${formatHelp(item.help)}</span></span>`
              : ''
          }${
            item.details
              ? `<button class="fn-details-toggle" data-details="${idx}" aria-expanded="false" aria-controls="details-row-${idx}" title="Show details"><span class="fn-chevron">▾</span></button>`
              : ''
          }</td>`;

      // Pill toggle: native checkbox (keeps delegated handler + free keyboard)
      // visually hidden, with role="switch" + aria-checked for screen readers.
      const toggleCell = `
        <td class="fn-toggle-cell" data-label="On">
          <label class="fn-switch">
            <input type="checkbox" ${item.enabled ? 'checked' : ''} data-field="enabled" data-idx="${idx}" role="switch" aria-checked="${item.enabled}">
            <span class="fn-track"></span><span class="fn-knob"></span>
          </label>
        </td>`;

      const mainRow = `
      <tr class="${item.enabled ? '' : 'fn-disabled-row'}${newRowIds.has(item.id) ? ' is-new' : ''}">
        ${toggleCell}
        <td class="fn-dot-cell" data-label=""><span class="fn-color-dot" style="background:${colorFor(idx)}"></span></td>
        ${labelCell}
        <td data-label="Unit cost ($)"><input type="number" min="0" step="0.01" value="${item.unitCost}" data-field="unitCost" data-idx="${idx}"></td>
        ${rateCell}
        <td data-label="Start mo"><input type="number" min="0" step="1" value="${item.startMonth}" data-field="startMonth" data-idx="${idx}"></td>
        ${endCell}
        ${item.custom ? `<td data-label=""><button class="fn-remove-btn" data-remove="${idx}" title="Remove" aria-label="Remove ${escapeHtml(item.label)}">✕</button></td>` : '<td data-label=""></td>'}
      </tr>`;

      // Optional accordion details row (for long content like daycare's Canada breakdown).
      const detailsRow = item.details
        ? `<tr class="fn-details-row" id="details-row-${idx}" hidden>
            <td colspan="8"><div class="fn-details-body">${formatHelp(item.details)}</div></td>
          </tr>`
        : '';

      return mainRow + detailsRow;
    })
    .join('');
  // Clear the one-shot "new row" flag after the entrance animation applies.
  newRowIds.clear();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/**
 * Format help text for the tooltip: escape HTML, then convert the plain-text
 * structure (newlines + bullet markers "•") into styled HTML. Lines starting
 * with "•" become <span class="fn-help-bullet"> rows; other lines become
 * paragraphs. Keeps multi-line tooltips like the daycare Canada breakdown
 * readable instead of a wall of text.
 */
function formatHelp(text: string): string {
  const escaped = escapeHtml(text);
  return escaped
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (trimmed === '') return '';
      if (trimmed.startsWith('•')) {
        return `<span class="fn-help-bullet">${trimmed}</span>`;
      }
      return `<span class="fn-help-line">${trimmed}</span>`;
    })
    .join('');
}

/**
 * Simple animation: a single short fade on first render, near-instant on edits.
 * The earlier progressive line-draw + staggered entrance was distracting and
 * obscured the values while it played — per feedback, clarity beats flourish.
 */
function buildChartAnimation(isFirst: boolean): Record<string, unknown> {
  return { duration: isFirst ? 400 : 150, easing: 'easeOutQuart' };
}

let firstChartRender = true;

function renderChart(result: BabyCalcResult): void {
  // Fold small series into an "Other" band so the chart stays legible (≤5
  // bands total). Pure transform — see groupSmallSeries in baby.ts. Source
  // for the rule: UK Gov Analysis Function (≤4 stacks) + ONS (≤5 segments).
  const grouped = groupSmallSeries(result);
  const labels = grouped.monthLabels.map((m) => formatMonth(m));

  // Stacked area: each band is a series' cumulative cost, stacked so the top
  // edge = grand total at every point. Largest band on the baseline.
  const datasets: ChartDataset[] = grouped.series.map((s, bandIdx) => {
    const color = s.isOther ? OTHER_COLOR : colorFor(items.findIndex((it) => it.id === s.id));
    return {
      label: s.label,
      data: s.cumulative,
      borderColor: color,
      backgroundColor: color + 'd9', // ~85% opacity solid band
      fill: true,
      tension: 0.3,
      pointRadius: 0,
      pointHoverRadius: 4,
      borderWidth: 1.5,
      // Carry the Other member list so the tooltip can show the breakdown.
      _members: s.isOther ? s.members : undefined,
      _bandIndex: bandIdx,
    };
  });

  if (chart) chart.destroy();
  const isFirst = firstChartRender;
  chart = new Chart(document.getElementById('cost-chart') as HTMLCanvasElement, {
    type: 'line',
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: buildChartAnimation(isFirst),
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'bottom',
          labels: { boxWidth: 12, font: { size: 11 }, usePointStyle: true },
        },
        tooltip: {
          callbacks: {
            // For the "Other" band, list what's inside it; otherwise show the
            // band's cumulative value. Footer always shows the running total.
            label: (ctx: { dataset: ChartDataset; parsed: { y: number } }) => {
              const members = ctx.dataset._members as
                | Array<{ id: string; label: string; total: number }>
                | undefined;
              if (members && members.length > 0) {
                const breakdown = members
                  .map((m) => `   ${m.label}: ${formatCurrency(m.total, 0)}`)
                  .join('\n');
                return `Other (${formatCurrency(ctx.parsed.y, 0)}):\n${breakdown}`;
              }
              return `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y, 0)}`;
            },
            footer: (items: Array<{ parsed: { y: number } }>) => {
              const sum = items.reduce((a, it) => a + it.parsed.y, 0);
              return `Total: ${formatCurrency(sum, 0)}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          title: { display: true, text: "Child's age" },
          ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
          grid: { display: false },
        },
        y: {
          stacked: true,
          title: { display: true, text: 'Cumulative cost (stacked)' },
          ticks: { callback: (v: number | string) => formatCurrency(Number(v), 0) },
        },
      },
    },
  });
  // The entrance animation only plays once; subsequent edits use a short duration.
  firstChartRender = false;
}

function render(): void {
  const { start, end } = readWindow();
  const result = calculateBabyCosts({ windowStartMonth: start, windowEndMonth: end, items });
  grandTotalEl.textContent = formatCurrency(result.grandTotal, 0);
  renderChart(result);
  syncUrl();
}

/* ------------------------------ mutations ------------------------------- */

function updateItem(idx: number, field: keyof BabyItem, value: string | boolean): void {
  const item = items[idx];
  if (!item) return;
  switch (field) {
    case 'enabled':
      item.enabled = value as boolean;
      break;
    case 'label':
      item.label = String(value);
      break;
    case 'unitCost':
      item.unitCost = Math.max(0, Number(value) || 0);
      break;
    case 'rate':
      item.rate = Math.max(0, Number(value) || 0);
      break;
    case 'ratePeriod':
      item.ratePeriod = value as BabyRatePeriod;
      break;
    case 'startMonth':
      item.startMonth = Math.max(0, Math.floor(Number(value) || 0));
      break;
    case 'endMonth':
      item.endMonth = Math.floor(Number(value) || -1);
      break;
    default:
      break;
  }
}

function addCustomItem(kind: BabyItemKind): void {
  const item: BabyItem = {
    id: newCustomId(),
    label: kind === 'onetime' ? 'New one-time cost' : 'New recurring cost',
    kind,
    enabled: true,
    unitCost: kind === 'onetime' ? 100 : 10,
    rate: kind === 'consumable' ? 1 : 1,
    ratePeriod: 'day',
    startMonth: 0,
    endMonth: kind === 'onetime' ? -1 : 12,
    custom: true,
  };
  items.push(item);
  newRowIds.add(item.id); // mark for the entrance animation
  renderItemsTable();
  render();
}

function removeItem(idx: number): void {
  const row = itemsTbody.rows[idx];
  // Animate the row out, then splice + re-render after the transition ends.
  // Falls back to instant removal if the row or reduced-motion makes it a no-op.
  if (row) {
    row.classList.add('is-leaving');
    const finish = () => {
      items.splice(idx, 1);
      renderItemsTable();
      render();
    };
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      finish();
    } else {
      row.addEventListener('transitionend', finish, { once: true });
      // Safety: if transitionend never fires (e.g. display:none race), force it.
      setTimeout(finish, 350);
    }
  } else {
    items.splice(idx, 1);
    renderItemsTable();
    render();
  }
}

/* ------------------------------ event wiring ---------------------------- */

// Delegated handler for all table inputs (edit-in-place).
itemsTbody.addEventListener('input', (e) => {
  const target = e.target as HTMLElement;
  const idxAttr = target.getAttribute('data-idx');
  const field = target.getAttribute('data-field') as keyof BabyItem | null;
  if (idxAttr === null || !field) return;
  const idx = Number(idxAttr);
  if (target instanceof HTMLInputElement) {
    if (target.type === 'checkbox') {
      updateItem(idx, field, target.checked);
      // Keep the switch's aria-checked in sync with the visual state.
      target.setAttribute('aria-checked', String(target.checked));
    } else {
      updateItem(idx, field, target.value);
    }
  } else if (target instanceof HTMLSelectElement) {
    updateItem(idx, field, target.value);
  }
  render();
});

itemsTbody.addEventListener('change', (e) => {
  // Re-render the table on toggle change to update the disabled-row styling.
  const target = e.target as HTMLElement;
  if (target.getAttribute('data-field') === 'enabled') {
    renderItemsTable();
  }
});

itemsTbody.addEventListener('click', (e) => {
  const removeBtn = (e.target as HTMLElement).closest('[data-remove]') as HTMLElement | null;
  if (removeBtn) {
    removeItem(Number(removeBtn.getAttribute('data-remove')));
    return;
  }
  // Accordion details toggle: show/hide the expansion row under an item.
  const detailsBtn = (e.target as HTMLElement).closest('[data-details]') as HTMLElement | null;
  if (detailsBtn) {
    const idx = detailsBtn.getAttribute('data-details');
    const row = idx !== null ? document.getElementById(`details-row-${idx}`) : null;
    if (row) {
      const willShow = row.hasAttribute('hidden');
      if (willShow) row.removeAttribute('hidden'); else row.setAttribute('hidden', '');
      detailsBtn.setAttribute('aria-expanded', String(willShow));
      detailsBtn.classList.toggle('is-open', willShow);
    }
  }
});

[windowStartInput, windowEndInput].forEach((el) => {
  el.addEventListener('input', render);
  el.addEventListener('change', render);
});

addRecurringBtn.addEventListener('click', () => addCustomItem('consumable'));
addOnetimeBtn.addEventListener('click', () => addCustomItem('onetime'));

document.getElementById('share-btn')?.addEventListener('click', () => {
  // encodeState() captures the FULL state (window + every item field), so the
  // copied link reproduces the calculator exactly — not just the window.
  copyShareableUrl(encodeState()).then(() => {
    const btn = document.getElementById('share-btn') as HTMLButtonElement;
    btn.textContent = '✅ Copied!';
    setTimeout(() => {
      btn.textContent = '📋 Copy shareable link';
    }, 2000);
  });
});

/* ----------------------------- initialization --------------------------- */

const params = getQueryParams({
  wstart: String(DEFAULT_WINDOW_START),
  wend: String(DEFAULT_WINDOW_END),
  items: '',
});
windowStartInput.value = params.wstart;
windowEndInput.value = params.wend;

// Restore the full item list from the URL if present; else use defaults.
const restored = decodeItems(params.items || undefined);
if (restored && restored.length > 0) {
  items = restored;
}

renderItemsTable();
render();
